import { Button } from '@citizen-walk/shared-ui';
import type {
  AiVerification,
  AiVerificationAnswerValue,
  AiVerificationQuestion,
} from '@citizen-walk/shared-types';
import { useEffect, useMemo, useState } from 'react';
import {
  fetchVerificationTemplateFull,
  type VerificationTemplateFull,
} from './useVerificationResults';
import { fetchVerificationsForPost } from './useAssignmentSetEditor';

/**
 * 投稿ごとの個別回答モーダル (Admin Wave AW4、docs/108 Critical #14)。
 *
 * 旧 `old2/apps/admin/src/pages/VerificationResultsPage.tsx` の `PostDetailModal` / `AnswerCell` を
 * v2 に踏襲。投稿の verifications サブコレクションを読み、参加者×質問の回答表を表示する:
 *   - 行 = 参加者 (UID 末尾 8 文字、hover で full UID)
 *   - 自他 = 回答者が投稿者本人か (post.authorUid と participantUid を比較)
 *   - 完了 = completed フラグ
 *   - perPost / perClassification の回答を ✓妥当 / ─中立 / ✗不当 等の記号で表示
 *   - 自由コメント + 送信日時
 *
 * 質問ラベル解決にはテンプレ (最初の verification の templateId) を使う。
 */

export interface PostDetailModalPost {
  id: string;
  title: string;
  authorNickname: string;
  authorUid: string;
}

export interface PostDetailModalProps {
  districtId: string;
  post: PostDetailModalPost;
  onClose: () => void;
}

export function PostDetailModal({ districtId, post, onClose }: PostDetailModalProps) {
  const [verifications, setVerifications] = useState<AiVerification[] | null>(null);
  const [template, setTemplate] = useState<VerificationTemplateFull | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetchVerificationsForPost(districtId, post.id)
      .then(async (vs) => {
        if (cancelled) return;
        setVerifications(vs);
        // 最初の verification の templateId からテンプレを解決
        const templateId = vs[0]?.templateId ?? null;
        if (templateId) {
          const tpl = await fetchVerificationTemplateFull(districtId, templateId);
          if (!cancelled) setTemplate(tpl);
        }
      })
      .catch((e) => {
        if (!cancelled) setError((e as Error).message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [districtId, post.id]);

  const perPostQuestions = useMemo(
    () => (template?.perPostQuestions ?? []).filter((q) => q.enabled),
    [template],
  );
  const perClsQuestions = useMemo(
    () => (template?.perClassificationQuestions ?? []).filter((q) => q.enabled),
    [template],
  );

  const allRanksUsed = useMemo(() => {
    const ranks = new Set<string>();
    if (verifications) {
      for (const v of verifications) {
        for (const r of Object.keys(v.perClassificationAnswers ?? {})) ranks.add(r);
      }
    }
    return [...ranks].sort((a, b) => Number(a) - Number(b));
  }, [verifications]);

  return (
    <div
      className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label={`${post.title || '(無題)'} の個別回答`}
    >
      <div
        className="bg-white w-full max-w-6xl max-h-[90vh] overflow-auto rounded-xl p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-between items-start gap-2 mb-3">
          <div className="min-w-0">
            <h2 className="text-lg font-bold truncate text-slate-900">{post.title || '(無題)'}</h2>
            <p className="text-xs text-slate-500">
              投稿者: {post.authorNickname || '(匿名)'}{' '}
              <span className="font-mono" title={post.authorUid}>
                …{post.authorUid.slice(-8)}
              </span>{' '}
              · 投稿 ID: <span className="font-mono">{post.id}</span>
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="閉じる"
            className="text-slate-400 hover:text-slate-700 text-2xl leading-none"
          >
            ×
          </button>
        </div>

        {loading && <p className="text-sm text-slate-600">読込中…</p>}

        {error && (
          <div className="border border-danger-300 bg-danger-50 rounded p-3 text-sm text-danger-700">
            ⚠ 取得失敗: {error}
          </div>
        )}

        {!loading && !error && verifications && verifications.length === 0 && (
          <p className="text-sm text-slate-500">この投稿への検証回答はまだありません。</p>
        )}

        {!loading && verifications && verifications.length > 0 && (
          <>
            <p className="text-xs text-slate-500 mb-2">
              参加者 {verifications.length} 名分の回答（テンプレ:{' '}
              {template?.displayName ?? '(不明)'}）
            </p>
            <div className="overflow-x-auto border border-slate-200 rounded">
              <table className="text-xs border-collapse min-w-full">
                <thead>
                  <tr className="bg-slate-100">
                    <th className="text-left p-2 border-r border-b border-slate-200 sticky left-0 bg-slate-100 z-10 min-w-[10rem]">
                      参加者
                    </th>
                    <th className="text-left p-2 border-r border-b border-slate-200">自他</th>
                    <th className="text-left p-2 border-r border-b border-slate-200">完了</th>
                    {perPostQuestions.map((q) => (
                      <th
                        key={`pp-${q.id}`}
                        className="text-left p-2 border-r border-b border-slate-200 min-w-[10rem]"
                        title={q.helpText ?? ''}
                      >
                        <div className="font-semibold">{q.label || '(無題)'}</div>
                        <div className="text-[10px] text-slate-500">{q.type}</div>
                      </th>
                    ))}
                    {allRanksUsed.flatMap((rank) =>
                      perClsQuestions.map((q) => (
                        <th
                          key={`pc-${rank}-${q.id}`}
                          className="text-left p-2 border-r border-b border-slate-200 min-w-[10rem] bg-amber-50"
                          title={q.helpText ?? ''}
                        >
                          <div className="font-semibold">
                            #{rank} {q.label || '(無題)'}
                          </div>
                          <div className="text-[10px] text-slate-500">{q.type}</div>
                        </th>
                      )),
                    )}
                    <th className="text-left p-2 border-r border-b border-slate-200 min-w-[12rem]">
                      自由コメント
                    </th>
                    <th className="text-left p-2 border-b border-slate-200 min-w-[10rem]">
                      送信日時
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {verifications
                    .slice()
                    .sort((a, b) => a.submittedAt.localeCompare(b.submittedAt))
                    .map((v) => {
                      const isOwn = !!post.authorUid && v.participantUid === post.authorUid;
                      return (
                        <tr key={v.participantUid} className="hover:bg-slate-50">
                          <td
                            className="p-2 border-r border-b border-slate-200 font-mono sticky left-0 bg-white"
                            title={v.participantUid}
                          >
                            …{v.participantUid.slice(-8)}
                          </td>
                          <td
                            className={`p-2 border-r border-b border-slate-200 ${
                              isOwn ? 'text-primary-700 font-semibold' : 'text-slate-500'
                            }`}
                            title={isOwn ? '回答者と投稿者が一致' : '他者の投稿に対する回答'}
                          >
                            {isOwn ? '自分' : '他者'}
                          </td>
                          <td className="p-2 border-r border-b border-slate-200">
                            {v.completed ? '✓' : '—'}
                          </td>
                          {perPostQuestions.map((q) => (
                            <td
                              key={`pp-${q.id}`}
                              className="p-2 border-r border-b border-slate-200"
                            >
                              <AnswerCell q={q} ans={v.perPostAnswers?.[q.id]} />
                            </td>
                          ))}
                          {allRanksUsed.flatMap((rank) =>
                            perClsQuestions.map((q) => (
                              <td
                                key={`pc-${rank}-${q.id}`}
                                className="p-2 border-r border-b border-slate-200 bg-amber-50/30"
                              >
                                <AnswerCell
                                  q={q}
                                  ans={v.perClassificationAnswers?.[rank]?.[q.id]}
                                />
                              </td>
                            )),
                          )}
                          <td className="p-2 border-r border-b border-slate-200 whitespace-pre-wrap break-words">
                            {v.freeComment || ''}
                          </td>
                          <td className="p-2 border-b border-slate-200 whitespace-nowrap text-slate-600">
                            {v.submittedAt ? new Date(v.submittedAt).toLocaleString('ja-JP') : ''}
                          </td>
                        </tr>
                      );
                    })}
                </tbody>
              </table>
            </div>
            <p className="text-[11px] text-slate-500 mt-2">
              ※ 参加者列の「…XXXX」は UID 末尾 8 文字。フル UID はマウスホバーで確認できます。
              「自他」列は回答者が投稿者本人かどうかを示します。
            </p>
          </>
        )}

        <div className="flex justify-end mt-4">
          <Button variant="secondary" size="sm" onClick={onClose}>
            閉じる
          </Button>
        </div>
      </div>
    </div>
  );
}

/** 1 つの回答セル。質問形式に応じて記号 + 色で簡潔に表示 (old2 AnswerCell 踏襲)。 */
export function AnswerCell({
  q,
  ans,
}: {
  q: AiVerificationQuestion;
  ans: AiVerificationAnswerValue | undefined;
}) {
  if (!ans) return <span className="text-slate-300">—</span>;
  switch (ans.type) {
    case 'three_state': {
      if (ans.value === 'valid')
        return <span className="text-success-700 font-semibold">✓ 妥当</span>;
      if (ans.value === 'neutral') return <span className="text-slate-600">─ どちらでもない</span>;
      return <span className="text-danger-700 font-semibold">✗ 妥当ではない</span>;
    }
    case 'binary':
      return ans.value === 'valid' ? (
        <span className="text-success-700 font-semibold">✓ 妥当</span>
      ) : (
        <span className="text-danger-700 font-semibold">✗ 妥当ではない</span>
      );
    case 'five_scale':
      return <span className="font-semibold">{ans.value}</span>;
    case 'yes_no':
      return ans.value ? (
        <span className="text-success-700">はい</span>
      ) : (
        <span className="text-danger-700">いいえ</span>
      );
    case 'comment':
      return <span className="whitespace-pre-wrap break-words">{ans.value || ''}</span>;
    case 'custom_choice': {
      const labels = (q.choices ?? []).reduce<Record<string, string>>((acc, c) => {
        acc[c.value ?? c.label] = c.label;
        return acc;
      }, {});
      const values = Array.isArray(ans.value) ? ans.value : [];
      const display = values.map((v) => labels[v] ?? v);
      return (
        <span>
          {display.join(', ')}
          {ans.other ? ` / その他: ${ans.other}` : ''}
        </span>
      );
    }
    default:
      return <span className="text-slate-400">?</span>;
  }
}
